// # Command Line: Integration of dynamic column sorting and the 10,000 record limit control for Transfer Report.
import type { TablePaginationConfig } from "antd/es/table";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { getTenantId } from "../../../redux/features/auth/authSlice";
import {
  useGetTrackNTraceAsnLinesQuery,
  useGetTrackNTraceCountsByUserQuery,
} from "../../../redux/services/trackntraceApiSlice";
import { removeEmptyKeys } from "../../../utils";
import { useTenantSitesAndZones } from "../../../hooks/useTenantSitesAndZones";
import { UICalendar } from "../../common/uiCalendar";
import {
  buildTransferReportRows,
  type TransferAsnRow,
} from "./asnReportHelpers";
import "./style.css";
import { exportRowsToWorkbook } from "./exportWorkbook";
import { ReportToolbar, ReportFilterChips } from "../../common/reportToolbar";
import Select from "../../common/selectInput/Select";
import Input from "../../common/input/Input";
import { MetricCard } from "./components/MetricCard";
import {
  FileStack,
  PackageCheck,
  Truck,
  X,
} from "lucide-react";
import { MetricSkeleton } from "./components/MetricSkeleton";
import ShippingNoticeDetail from "./components/ShippingNoticeDetail";
import ResponsiveTransfersList from "./components/ResponsiveTransfersList";

const TransferReport = () => {
  const tenantId = useSelector(getTenantId);
  const {
    zoneRows,
    siteNameById,
    zoneNameById,
    siteOptionsById,
    zoneOptionsById,
  } = useTenantSitesAndZones(tenantId);

  const [asn, setAsn] = useState("");
  const [selectedAsn, setSelectedAsn] = useState<any>(null);

  const [searchValue, setSearchValue] = useState("");
  const [appliedSearchValue, setAppliedSearchValue] = useState("");

  const [siteId, setSiteId] = useState<string | undefined>(undefined);
  const [zoneId, setZoneId] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState<moment.Moment[]>([]);
  const [appliedAsn, setAppliedAsn] = useState("");
  const [appliedSiteId, setAppliedSiteId] = useState<string | undefined>(
    undefined,
  );
  const [appliedZoneId, setAppliedZoneId] = useState<string | undefined>(
    undefined,
  );
  const [appliedDateRange, setAppliedDateRange] = useState<moment.Moment[]>([]);

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const queryArgs = selectedAsn
    ? {
        asn: selectedAsn.asn,
        tenantId: selectedAsn.tenantId,
        transferredFromSite: selectedAsn.transferredFromSite,
        transferredFromZone: selectedAsn.transferredFromZone,
        transferredToSite: selectedAsn.transferredToSite,
        transferredToZone: selectedAsn.transferredToZone,
        from: selectedAsn.from,
        to: selectedAsn.to,
        identityId: selectedAsn.identityId,
      }
    : {};

  const { data: ASNData, isFetching: fetchASN } =
    useGetTrackNTraceAsnLinesQuery(
      queryArgs as { asn: string; [key: string]: unknown },
      {
        skip: !selectedAsn?.asn,
      },
    );

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortConfig, setSortConfig] = useState<{
    field?: keyof TransferAsnRow;
    order?: "ascend" | "descend";
  }>({});

  const transferQueryParams = useMemo(
    () =>
      removeEmptyKeys({
        tenantId: tenantId || undefined,
        reportType: "transfer",
        page: 1,
        asn: (appliedSearchValue || appliedAsn).trim() || undefined,
        limit: 10000,
        from: appliedDateRange?.length
          ? moment.utc(appliedDateRange[0]).format("YYYY-MM-DD")
          : undefined,
        to: appliedDateRange?.length
          ? moment.utc(appliedDateRange[1]).format("YYYY-MM-DD")
          : undefined,
      }),
    [tenantId, appliedSearchValue, appliedAsn, appliedDateRange],
  );

  const { data: transferData, isFetching } = useGetTrackNTraceCountsByUserQuery(
    transferQueryParams,
    {
      skip: !tenantId,
    },
  );

  const transferResult = useMemo(
    () => transferData?.result ?? transferData,
    [transferData],
  );
  const groupedRows = useMemo(
    () => buildTransferReportRows(transferResult),
    [transferResult],
  );
  const resolvedRows = useMemo(() => {
    const resolveById = (rawIds: string, lookupMap: Map<string, string>) => {
      if (!rawIds) return "";
      const names = rawIds
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean)
        .map((id) => lookupMap.get(id) || id);
      return Array.from(new Set(names)).join(", ");
    };

    const pickResolvedValue = (
      explicitValue: string,
      idValue: string,
      lookupMap: Map<string, string>,
      fallbackValue: string,
    ) => {
      const explicit = explicitValue.trim();
      const resolvedFromId = resolveById(idValue, lookupMap);
      if (resolvedFromId) return resolvedFromId;
      if (
        !explicit ||
        explicit.toLowerCase() === "site" ||
        explicit.toLowerCase() === "zone"
      ) {
        return fallbackValue.trim();
      }
      return explicit;
    };

    return groupedRows.map((row) => ({
      ...row,
      transferredFromSite: pickResolvedValue(
        row.transferredFromSite,
        row.transferredFromSiteId,
        siteNameById,
        row.transferredFrom,
      ),
      transferredFromZone: pickResolvedValue(
        row.transferredFromZone,
        row.transferredFromZoneId,
        zoneNameById,
        "",
      ),
      transferredToSite: pickResolvedValue(
        row.transferredToSite,
        row.transferredToSiteId,
        siteNameById,
        row.transferredTo,
      ),
      transferredToZone: pickResolvedValue(
        row.transferredToZone,
        row.transferredToZoneId,
        zoneNameById,
        "",
      ),
    }));
  }, [groupedRows, siteNameById, zoneNameById]);
  const tableRows = useMemo(() => {
    const siteName = appliedSiteId
      ? (siteNameById.get(appliedSiteId) ?? "")
      : "";
    const zoneName = appliedZoneId
      ? (zoneNameById.get(appliedZoneId) ?? "")
      : "";
    const siteQuery = siteName.trim().toLowerCase();
    const zoneQuery = zoneName.trim().toLowerCase();
    return resolvedRows.filter((r) => {
      const siteHaystack =
        `${r.transferredFromSite} ${r.transferredToSite} ${r.transferredFrom} ${r.transferredTo}`.toLowerCase();
      const zoneHaystack =
        `${r.transferredFromZone} ${r.transferredToZone}`.toLowerCase();
      const siteMatch = !siteQuery || siteHaystack.includes(siteQuery);
      const zoneMatch = !zoneQuery || zoneHaystack.includes(zoneQuery);
      return siteMatch && zoneMatch;
    });
  }, [
    resolvedRows,
    appliedSiteId,
    appliedZoneId,
    siteNameById,
    zoneNameById,
  ]);

  const sortedRows = useMemo(() => {
    if (!sortConfig.field || !sortConfig.order) return tableRows;
    const dir = sortConfig.order === "ascend" ? 1 : -1;
    const field = sortConfig.field;
    return [...tableRows].sort((a, b) => {
      if (field === "qty")
        return (Number(a.qty ?? 0) - Number(b.qty ?? 0)) * dir;
      if (field === "dateTime")
        return (
          (moment(a.dateTime).valueOf() - moment(b.dateTime).valueOf()) * dir
        );
      return String(a[field] ?? "").localeCompare(String(b[field] ?? "")) * dir;
    });
  }, [tableRows, sortConfig]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize]);

  useEffect(() => {
    if (pagedRows?.length > 0) {
      setSelectedAsn(pagedRows[0]);
    }
  }, [pagedRows]);

  const totalCount = tableRows.length;

  const applyFilters = () => {
    setAppliedAsn(asn.trim());
    setAppliedSiteId(siteId);
    setAppliedZoneId(zoneId);
    setAppliedDateRange(dateRange);
    setPage(1);
    setShowAdvancedFilters(false);
  };

  const applySearch = (value: string) => {
    const targetSearch = value.trim();

    setAppliedSearchValue((prev) => {
      if (prev === targetSearch) return prev;

      setPage(1);

      return targetSearch;
    });
  };

  const resetFilters = () => {
    setAsn("");
    setSiteId(undefined);
    setZoneId(undefined);
    setDateRange([]);
    setSearchValue("");
    setAppliedSearchValue("");
    setAppliedAsn("");
    setAppliedSiteId(undefined);
    setAppliedZoneId(undefined);
    setAppliedDateRange([]);
    setPage(1);
    setPageSize(50);
  };

  const activeFilterChips = useMemo(() => {
    const chips: { label: string; onRemove: () => void }[] = [];

    if (appliedAsn) {
      chips.push({
        label: `ASN: ${appliedAsn}`,
        onRemove: () => {
          setAsn("");
          setAppliedAsn("");
          setPage(1);
        },
      });
    }

    if (appliedSiteId) {
      chips.push({
        label: `Site: ${siteNameById.get(appliedSiteId) ?? appliedSiteId}`,
        onRemove: () => {
          setSiteId(undefined);
          setAppliedSiteId(undefined);
          setPage(1);
        },
      });
    }

    if (appliedZoneId) {
      chips.push({
        label: `Zone: ${zoneNameById.get(appliedZoneId) ?? appliedZoneId}`,
        onRemove: () => {
          setZoneId(undefined);
          setAppliedZoneId(undefined);
          setPage(1);
        },
      });
    }

    if (appliedDateRange?.length === 2) {
      chips.push({
        label: `Date: ${moment(appliedDateRange[0]).format(
          "DD/MM/YYYY",
        )} - ${moment(appliedDateRange[1]).format("DD/MM/YYYY")}`,
        onRemove: () => {
          setDateRange([]);
          setAppliedDateRange([]);
          setPage(1);
        },
      });
    }

    return chips;
  }, [
    appliedAsn,
    appliedSiteId,
    appliedZoneId,
    appliedDateRange,
    siteNameById,
    zoneNameById,
  ]);

  const exportExcel = () => {
    const header = [
      "ASN",
      "Transferred From Site",
      "Transferred From Zone",
      "Transferred To Site",
      "Transferred To Zone",
      "QTY",
      "Date and Time",
    ];
    const rows: Array<Array<string | number>> = [
      header,
      ...tableRows.map((r) => [
        r.asn,
        r.transferredFromSite || r.transferredFrom || "-",
        r.transferredFromZone || "-",
        r.transferredToSite || r.transferredTo || "-",
        r.transferredToZone || "-",
        r.qty,
        r.dateTime,
      ]),
    ];
    exportRowsToWorkbook(
      `transfer-report-${moment().format("YYYY-MM-DD")}.xlsx`,
      "Transfer Report",
      rows,
    );
  };

  return (
    <div className="scTheme h-full flex flex-col min-h-0  rounded-md gap-0">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 shrink-0">
        {isFetching ? (
          <>
            <MetricSkeleton />
            <MetricSkeleton />
            <MetricSkeleton />
          </>
        ) : (
          <>
            <MetricCard
              label="TOTAL ACTIVE ASN's"
              value={totalCount}
              accent="default"
              helperText=""
              icon={<FileStack />}
            />
            <MetricCard
              label="IN TRANSIT"
              value={transferData?.result?.summary?.inTransit ?? 0}
              accent="orange"
              helperText=""
              icon={<Truck />}
            />
            <MetricCard
              label="DELIVERED"
              helperText=""
              accent="green"
              value={transferData?.result?.summary?.delivered ?? 0}
              icon={<PackageCheck />}
            />
          </>
        )}
      </div>
      <div className="mt-3">
        <ReportToolbar
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          onDebounceSearch={(value) => applySearch(value)}
          searchPlaceholder="Search ASN"
          onSearchEnter={applySearch}
          showAdvancedFilters={showAdvancedFilters}
          onToggleAdvancedFilters={() =>
            setShowAdvancedFilters((show) => !show)
          }
          onApplyFilters={applyFilters}
          onResetFilters={resetFilters}
          activeFilterCount={activeFilterChips.length}
          onExportExcel={exportExcel}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {/* Site */}
            <div className="w-full">
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                Site
              </label>
              <Select
                placeholder="Select Site"
                value={siteId}
                onChange={(e) => {
                  const value = e.target.value;
                  setSiteId(value);
                  setZoneId("");
                }}
                onClear={() => {
                  setSiteId("");
                  setZoneId("");
                }}
                allowClear
                options={siteOptionsById}
              />
            </div>

            {/* Zone */}
            <div className="w-full">
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                Zone
              </label>
              <Select
                placeholder="Select Zone"
                value={zoneId}
                onChange={(e) => {
                  const value = e.target.value;
                  setZoneId(value);

                  const z = zoneRows.find(
                    (r) => String(r.id) === String(value),
                  );

                  if (z?.siteId) {
                    setSiteId(String(z.siteId));
                  }
                }}
                onClear={() => setZoneId("")}
                allowClear
                options={zoneOptionsById}
              />
            </div>

            {/* Time Range */}
            <div className="w-full">
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                Time Range
              </label>
              <UICalendar
                params={dateRange}
                setParams={setDateRange}
                handleChange={(dates: moment.Moment[]) =>
                  setDateRange(dates || [])
                }
                className="w-full"
                hideArrows={true}
              />
            </div>
          </div>
        </ReportToolbar>
      </div>
 <ReportFilterChips chips={activeFilterChips} onClearAll={resetFilters} />

      <div className="flex h-[calc(100vh-2rem)] gap-4 pt-4 overflow-hidden bg-slate-50">
        {/* LEFT SIDE: Compact Master List */}
        <ResponsiveTransfersList
          pagedRows={pagedRows}
          setSelectedAsn={setSelectedAsn}
          currentAsn={selectedAsn?.asn}
          loading={isFetching}
        />

        {/* RIGHT SIDE: Beautiful Detail Card View */}
        <div className="flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-y-auto flex flex-col">
          <ShippingNoticeDetail
            ASNData={ASNData?.result}
            selectedAsn={selectedAsn}
            loading={isFetching || fetchASN}
          />
        </div>
      </div>
    </div>
  );
};

export default TransferReport;
